import { createAction, createSlice } from "@reduxjs/toolkit";

import { convertToCamelCase } from "../../utils/code-utils";
import {
  createCrewMember,
  deleteCrewMember,
  fetchCrewList,
  processCrewDocument,
  updateCrewMember,
} from "../thunks/crewThunks";

import type { CrewMember } from "../../utils/types/crew";

// Action types dispatched by storeChangeHandler for storeId:"crew" events
const crewWsPost = createAction<any>("crew/POST");
const crewWsDelete = createAction<any>("crew/DELETE");

const STAGE_LABELS: Record<string, string> = {
  queued: "Extraction queued — waiting for worker…",
  textract_started: "Analyzing document with Textract…",
  textract_done: "Document analysis complete, parsing records…",
  db_saving: "Saving crew records to database…",
  completed: "Crew extraction complete!",
  failed: "Extraction failed.",
};

export const getCrewStageLabel = (stage: string | null): string =>
  stage ? (STAGE_LABELS[stage] ?? `Processing: ${stage}…`) : "Processing document…";

interface CrewState {
  crewList: {
    [jobId: string]: {
      data: CrewMember[];
    };
  };
  loading: boolean;
  processing: boolean;        // HTTP POST in flight
  isExtractRunning: boolean;  // WebSocket-driven async extraction
  extractStage: string | null;
  extractMessage: string | null;
  extractingJobId: string | null; // Which jobId is currently being extracted (for correlation)
  earlyCompletionJobId: string | null; // Set when crew_complete/failed arrives before processCrewDocument.fulfilled
  refreshTrigger: number;
  error: string | null;
}

const initialState: CrewState = {
  crewList: {},
  loading: false,
  processing: false,
  isExtractRunning: false,
  extractStage: null,
  extractMessage: null,
  extractingJobId: null,
  earlyCompletionJobId: null,
  refreshTrigger: 0,
  error: null,
};

export const crewSlice = createSlice({
  name: "crew",
  initialState,
  reducers: {
    setCrewProgress(state, action) {
      const { job_id, stage, message } = action.payload;
      // Ignore events for other voyages
      if (job_id && state.extractingJobId && job_id !== state.extractingJobId) return;
      state.isExtractRunning = true;
      state.extractStage = stage ?? null;
      state.extractMessage = message ?? null;
      // Auto-set extractingJobId if a WS event arrives without a prior POST (e.g. page reload)
      if (job_id && !state.extractingJobId) {
        state.extractingJobId = job_id;
      }
    },
    setCrewComplete(state, action) {
      const { job_id, records } = action.payload ?? {};
      // Ignore events for other voyages
      if (job_id && state.extractingJobId && job_id !== state.extractingJobId) return;
      // Race condition guard: if processCrewDocument.fulfilled hasn't run yet, record this
      // early arrival so fulfilled knows to skip re-entering the spinner
      const isEarlyArrival = !state.extractingJobId && !!job_id;
      state.isExtractRunning = false;
      state.extractStage = null;
      state.extractMessage = null;
      state.extractingJobId = null;
      if (isEarlyArrival) {
        state.earlyCompletionJobId = job_id;
      }
      if (job_id && Array.isArray(records) && records.length > 0) {
        // Directly hydrate from WebSocket payload — no extra GET round-trip needed
        state.crewList[job_id] = { data: convertToCamelCase<CrewMember[]>(records) };
      } else {
        // Fallback: trigger a GET if records not provided in payload
        state.refreshTrigger += 1;
      }
    },
    setCrewFailed(state, action) {
      const { job_id, message } = action.payload ?? {};
      // Ignore events for other voyages
      if (job_id && state.extractingJobId && job_id !== state.extractingJobId) return;
      // Race condition guard: same early-arrival guard as setCrewComplete
      const isEarlyArrival = !state.extractingJobId && !!job_id;
      state.isExtractRunning = false;
      state.extractStage = null;
      state.extractMessage = null;
      state.extractingJobId = null;
      if (isEarlyArrival) {
        state.earlyCompletionJobId = job_id;
      }
      state.error = message || "Crew extraction failed.";
      state.refreshTrigger += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCrewList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCrewList.fulfilled, (state, action) => {
        state.crewList[action.payload.jobId] = { data: action.payload.data };
        state.loading = false;
        // WS-miss recovery: if extraction seemed stuck but data arrived, clear the spinner
        if (
          state.isExtractRunning &&
          action.payload.data.length > 0 &&
          action.payload.jobId === state.extractingJobId
        ) {
          state.isExtractRunning = false;
          state.extractStage = null;
          state.extractMessage = null;
          state.extractingJobId = null;
        }
      })
      .addCase(fetchCrewList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(processCrewDocument.pending, (state) => {
        state.processing = true;
        state.error = null;
        // Reset any stale early-completion flag from a previous run so it doesn't
        // accidentally suppress the spinner for this new attempt
        state.earlyCompletionJobId = null;
      })
      .addCase(processCrewDocument.fulfilled, (state, action) => {
        state.processing = false;
        const { jobId } = action.payload;
        // Race condition: if crew_complete/failed arrived via WS before this HTTP
        // response, skip re-entering the spinner — extraction is already done
        if (state.earlyCompletionJobId === jobId) {
          state.earlyCompletionJobId = null;
          return;
        }
        // Switch to WebSocket-driven tracking; show "queued" stage
        state.isExtractRunning = true;
        state.extractStage = "queued";
        state.extractMessage = null;
        state.extractingJobId = jobId;
      })
      .addCase(processCrewDocument.rejected, (state, action) => {
        state.processing = false;
        state.error = action.payload as string;
      });

    // ── storeChange events (backend sends one "crew/POST" per record saved) ───
    builder
      .addCase(crewWsPost, (state, action) => {
        const record = action.payload?.record;
        const jobId = record?.job_id;
        if (!record || !jobId) return;

        const member = convertToCamelCase<CrewMember>(record);

        // Initialise bucket if first record for this job
        if (!state.crewList[jobId]) {
          state.crewList[jobId] = { data: [] };
        }
        // Upsert: avoid duplicates on reconnect replay
        const idx = state.crewList[jobId].data.findIndex((m) => m.id === member.id);
        if (idx === -1) {
          state.crewList[jobId].data.push(member);
        } else {
          state.crewList[jobId].data[idx] = member;
        }

        // Clear extraction spinner as soon as real data arrives.
        // Handles both orderings relative to processCrewDocument.fulfilled:
        //  A) fulfilled already ran → extractingJobId === jobId → clear
        //  B) fulfilled hasn't run yet → set earlyCompletionJobId so it skips re-entering spinner
        if (state.isExtractRunning && state.extractingJobId === jobId) {
          state.isExtractRunning = false;
          state.extractStage = null;
          state.extractMessage = null;
          state.extractingJobId = null;
          state.earlyCompletionJobId = null;
        } else if (state.processing && !state.extractingJobId) {
          // Race: POST /crew hasn't fulfilled yet but data is already arriving
          state.earlyCompletionJobId = jobId;
        }
      })
      .addCase(crewWsDelete, (state, action) => {
        const jobId = action.payload?.record?.job_id;
        const recordId = action.payload?.value;
        if (!jobId || !recordId || !state.crewList[jobId]) return;
        state.crewList[jobId].data = state.crewList[jobId].data.filter(
          (m) => m.id !== recordId
        );
      });

    // ── Manual CRUD ───────────────────────────────────────────────────────────
    builder
      .addCase(createCrewMember.fulfilled, (state, action) => {
        const { jobId, member } = action.payload;
        if (!state.crewList[jobId]) state.crewList[jobId] = { data: [] };
        state.crewList[jobId].data.push(member);
      })
      .addCase(updateCrewMember.fulfilled, (state, action) => {
        const { jobId, member } = action.payload;
        if (!state.crewList[jobId]) return;
        const idx = state.crewList[jobId].data.findIndex((m) => m.id === member.id);
        if (idx !== -1) state.crewList[jobId].data[idx] = member;
      })
      .addCase(deleteCrewMember.fulfilled, (state, action) => {
        const { jobId, id } = action.payload;
        if (!state.crewList[jobId]) return;
        state.crewList[jobId].data = state.crewList[jobId].data.filter(
          (m) => m.id !== id
        );
      });
  },
});

export const { setCrewProgress, setCrewComplete, setCrewFailed } = crewSlice.actions;
export default crewSlice.reducer;
